import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { adminConfigSyncApi } from '../../services/api';
import DevOnlyGuard from '../../components/common/DevOnlyGuard';
import syncStatusBadge from './syncStatusBadge';
import {
  SlidersHorizontal, Play, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronRight, RotateCcw, AlertTriangle, Ban, Info, ShieldAlert, Loader2
} from 'lucide-react';
import StuiSelect from '../../components/common/StuiSelect';
import { formatTimestamp } from '../../utils/dateTime';

/**
 * Admin Config Sync — reconciles AppCfg_* configuration between the preprod and prod
 * ADMIN databases (DevPlan step 86).
 *
 * SAFETY, per Danny 2026-08-06:
 *   - Every row starts UNCHECKED. Clicking straight through applies nothing.
 *   - An acknowledgement checkbox naming the target database must be ticked before Apply
 *     enables. (A typed-database-name modal sat behind this initially; Danny cut it as too
 *     much friction on top of a checkbox that already names the target.)
 *   - The acknowledgement resets on apply, on direction change, and on any selection change,
 *     so it can never carry from the rows the operator read to a different set.
 *   - Neither environment is the "safe" one. Prod and preprod get identical friction.
 *   - Rows whose foreign keys have no counterpart in the target are not selectable, and
 *     privilege-bearing fields are flagged — the review is the control.
 */
// Badge colour for a diff row's verdict.
const VERDICT_BADGE = { INSERT: 'stui-badge-green', UPDATE: 'stui-badge-blue' };

const AdminConfigSyncPage = () => {
  const [availability, setAvailability] = useState(null);
  const [direction, setDirection] = useState('prod-to-preprod');

  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState(null);

  // Selection is a Set of "table::key" tokens. Empty by default and never auto-populated.
  const [selected, setSelected] = useState(new Set());
  const [expandedTables, setExpandedTables] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  // Acknowledgement gate. A typed-database-name modal used to sit behind this; Danny cut it
  // as too much friction on top of a checkbox that already names the target database.
  const [acknowledged, setAcknowledged] = useState(false);

  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const isProdTarget = diff?.targetEnvironment === 'prod';

  // ── Gate reset ─────────────────────────────────────────────────────
  // Anything that changes WHAT would be written invalidates the acknowledgement, so it can
  // never carry over from the set of rows the operator actually read to a different one.
  const resetGates = useCallback(() => {
    setAcknowledged(false);
  }, []);

  // ── Data loading ───────────────────────────────────────────────────
  useEffect(() => {
    adminConfigSyncApi.getAvailability()
      .then(res => setAvailability(res.data))
      .catch(() => setAvailability({
        available: false,
        reason: 'Unable to reach the sync service.',
        environments: [], phases: []
      }));
  }, []);

  const loadHistory = useCallback(() => {
    adminConfigSyncApi.getHistory()
      .then(res => setHistory(res.data || []))
      .catch(() => setHistory([]));
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const loadDiff = useCallback(async (dir) => {
    setDiffLoading(true);
    setDiffError(null);
    setDiff(null);
    setSelected(new Set());
    setApplyResult(null);
    resetGates();
    try {
      const res = await adminConfigSyncApi.getDiff(dir);
      setDiff(res.data);
      // Open tables that actually have something to look at.
      const open = {};
      (res.data.tables || []).forEach(t => {
        if (t.rows?.some(r => r.verdict !== 'TARGET_ONLY')) open[t.table] = true;
      });
      setExpandedTables(open);
    } catch (err) {
      setDiffError(err.response?.data?.error || err.message);
    }
    setDiffLoading(false);
  }, [resetGates]);

  useEffect(() => {
    if (availability?.available) loadDiff(direction);
  }, [direction, availability, loadDiff]);

  // ── Selection ──────────────────────────────────────────────────────
  const tokenOf = (table, key) => `${table}::${key}`;

  const toggleRow = (table, key) => {
    setSelected(prev => {
      const next = new Set(prev);
      const t = tokenOf(table, key);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
    resetGates();
  };

  const setTableSelection = (t, on) => {
    setSelected(prev => {
      const next = new Set(prev);
      t.rows.filter(isSelectable)
        .forEach(r => on ? next.add(tokenOf(t.table, r.key)) : next.delete(tokenOf(t.table, r.key)));
      return next;
    });
    resetGates();
  };

  // A row is selectable only if it is actionable AND applicable. blockedReason means a
  // foreign key has no counterpart in the target, so there is nothing valid to write.
  const isSelectable = (r) => r.verdict !== 'TARGET_ONLY' && !r.blockedReason;

  const selectedRows = useMemo(() => {
    if (!diff) return [];
    const out = [];
    diff.tables.forEach(t => {
      if (t.blockedReason) return;          // whole table barred in this direction
      t.rows.forEach(r => {
        if (isSelectable(r) && selected.has(tokenOf(t.table, r.key)))
          out.push({ table: t.table, key: r.key, sourceFingerprint: r.sourceFingerprint });
      });
    });
    return out;
  }, [diff, selected]);

  const perTableCounts = useMemo(() => {
    const m = {};
    selectedRows.forEach(r => { m[r.table] = (m[r.table] || 0) + 1; });
    return m;
  }, [selectedRows]);

  // ── Apply ──────────────────────────────────────────────────────────
  const runApply = async () => {
    if (!diff || selectedRows.length === 0 || !acknowledged || applying) return;

    setApplying(true);
    try {
      // The server still verifies this against the catalog it resolved for the direction.
      // Auto-filled rather than typed, but it remains a real coherence check: if the client's
      // idea of the target has drifted from the server's, the apply is refused.
      const res = await adminConfigSyncApi.apply(direction, selectedRows, diff.targetDatabase);
      setApplyResult(res.data);
      loadHistory();
      // Re-diff so the page reflects reality rather than the pre-apply picture.
      const fresh = await adminConfigSyncApi.getDiff(direction);
      setDiff(fresh.data);
    } catch (err) {
      setApplyResult(err.response?.data?.status
        ? err.response.data
        : { status: 'FAILED', errorMessage: err.response?.data?.error || err.message, results: [] });
    }
    setSelected(new Set());
    resetGates();
    setApplying(false);
  };

  // ── Helpers ────────────────────────────────────────────────────────
  const canApply = selectedRows.length > 0 && acknowledged && !applying;

  const tablesWithWork = (diff?.tables || []).filter(t => !t.blockedReason && t.rows.length > 0);
  const blockedTables = (diff?.tables || []).filter(t => t.blockedReason);
  const totalApplicable = diff?.tables?.reduce(
    (n, t) => n + (t.blockedReason ? 0 : t.rows.filter(isSelectable).length), 0) || 0;
  const totalBlockedRows = diff?.tables?.reduce(
    (n, t) => n + (t.blockedReason ? 0 : t.rows.filter(r => r.blockedReason).length), 0) || 0;

  // ── Render ─────────────────────────────────────────────────────────
  // Block the whole section, not just the panels. Same treatment as the other dev-only pages.
  if (availability && !availability.available) {
    return <DevOnlyGuard pageName="Admin Config Sync" message={availability.reason} />;
  }

  return (
    <div className="dev-module sync-dashboard-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <SlidersHorizontal size={20} className="stui-imgupload-title-icon" />
            Admin Config Sync
          </h1>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="stui-steps-layout">

          {/* ═══════════ 1: SETUP ═══════════ */}
          <div className="stui-step-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">1</span>
              <h3>Setup</h3>
            </div>
            <p className="stui-step-desc">
              Compares <strong>AppCfg_*</strong> configuration between the two admin databases.
              Only the target is written.
            </p>

            <div className="stui-form-field">
              <label className="stui-label-xs stui-mb-xs" htmlFor="acs-direction">DIRECTION</label>
              <StuiSelect
                id="acs-direction"
                className="stui-field-input"
                value={direction}
                onChange={e => setDirection(e.target.value)}
                disabled={applying || diffLoading}
              >
                <option value="prod-to-preprod">Prod &rarr; PreProd</option>
                <option value="preprod-to-prod">PreProd &rarr; Prod</option>
              </StuiSelect>
            </div>

            {diff && (
              <div className={isProdTarget ? 'stui-inline-notice stui-inline-notice-warning' : 'stui-inline-notice'}>
                <AlertTriangle size={13} />
                <span>
                  Writes <strong>{diff.targetDatabase}</strong>. Reads{' '}
                  <strong>{diff.sourceDatabase}</strong> and never modifies it.
                </span>
              </div>
            )}

            <div className="stui-inline-notice">
              <Info size={13} />
              <span>
                This tool never deletes. Rows that exist only in the target are listed for
                information and are not actionable.
              </span>
            </div>

            <div>
              <div className="stui-subsection-divider" />
              <button className="stui-btn-ghost" onClick={() => setHistoryOpen(!historyOpen)}>
                {historyOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <Clock size={14} />
                History ({history.length})
              </button>
              {historyOpen && history.length > 0 && (
                <div className="stui-mt-xs">
                  <table className="stui-inner-table">
                    <thead>
                      <tr>
                        <th>Date</th><th>Direction</th>
                        <th>Status</th><th>Rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice(0, 10).map((h, i) => (
                        <tr key={i}>
                          <td>{(formatTimestamp(h.started_At) || '--')}</td>
                          <td>{h.project}</td>
                          <td>
                            <span className={syncStatusBadge(h.status)}>{h.status}</span>
                          </td>
                          <td>{h.objects_Applied ?? '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {historyOpen && history.length === 0 && (
                <div className="stui-text-italic stui-mt-xs">No admin config syncs have been run.</div>
              )}
            </div>
          </div>

          {/* ═══════════ 2: REVIEW ═══════════ */}
          <div className="stui-step-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">2</span>
              <h3>Review</h3>
            </div>

            {diffLoading && (
              <>
                <div className="stui-row-8 stui-fw-semibold stui-text-primary">
                  <Loader2 size={18} className="stui-spinning" />
                  <span>Comparing configuration...</span>
                </div>
              </>
            )}

            {diffError && (
              <div className="stui-inline-notice stui-inline-notice-error">
                <XCircle size={18} className="stui-text-error" />
                <div>
                  <div className="stui-fw-semibold">Could not build the diff</div>
                  <div className="stui-text-xs stui-text-muted">{diffError}</div>
                </div>
              </div>
            )}

            {diff?.fatalError && (
              <div className="stui-inline-notice stui-inline-notice-error">
                <ShieldAlert size={18} className="stui-text-error" />
                <div>
                  <div className="stui-fw-semibold">Diff refused</div>
                  <div className="stui-text-xs stui-text-muted">{diff.fatalError}</div>
                </div>
              </div>
            )}

            {diff && !diff.fatalError && totalApplicable === 0 && (
              <div className="stui-inline-notice stui-inline-notice-success">
                <CheckCircle size={18} className="stui-td-success" />
                <div>
                  <div className="stui-fw-semibold">No differences</div>
                  <div className="stui-text-xs stui-text-muted">
                    Configuration matches in this direction.
                  </div>
                </div>
              </div>
            )}

            {diff && !diff.fatalError && totalApplicable > 0 && (
              <>
                <p className="stui-step-desc">
                  <strong>{totalApplicable}</strong> row(s) can be applied.{' '}
                  <strong>{selectedRows.length}</strong> selected.
                  {selectedRows.length === 0 && ' Nothing is selected by default.'}
                  {totalBlockedRows > 0 && (
                    <> {totalBlockedRows} more differ but reference something the target
                    doesn&apos;t have.</>
                  )}
                </p>

                <>
                  {tablesWithWork.map(t => {
                    const actionable = t.rows.filter(r => r.verdict !== 'TARGET_ONLY');
                    const targetOnly = t.rows.filter(r => r.verdict === 'TARGET_ONLY');
                    const open = !!expandedTables[t.table];
                    const chosen = perTableCounts[t.table] || 0;

                    return (
                      <div key={t.table} className="stui-results-panel">
                        <div
                          className="stui-results-panel-header"
                          onClick={() => setExpandedTables(p => ({ ...p, [t.table]: !open }))}
                        >
                          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          <span className="stui-fw-semibold">{t.table}</span>
                          <span className="stui-ml-auto stui-text-xs stui-text-muted">
                            {actionable.length} to apply
                            {chosen > 0 && <strong> &middot; {chosen} selected</strong>}
                            {targetOnly.length > 0 && ` · ${targetOnly.length} target-only`}
                          </span>
                        </div>

                        {open && (
                          <div className="stui-results-panel-body">
                            <div className="stui-label-xs stui-mb-sm">
                              matched on <code>{t.keyColumns.join(' + ')}</code>
                            </div>

                            {actionable.length > 0 && (
                              <div className="stui-btn-row">
                                <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setTableSelection(t, true)}>
                                  Select all {actionable.length}
                                </button>
                                <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setTableSelection(t, false)}>
                                  Clear
                                </button>
                              </div>
                            )}

                            <div className="stui-divided-list">
                            {actionable.map(r => {
                              const tok = tokenOf(t.table, r.key);
                              const rowOpen = !!expandedRows[tok];
                              const blocked = !!r.blockedReason;
                              const hasSensitive = r.fields?.some(f => f.sensitive);
                              return (
                                <div key={tok} className={blocked ? 'stui-text-muted' : undefined}>
                                  <label className={`stui-checkbox${blocked ? ' stui-text-muted' : ''}`}>
                                    <input
                                      type="checkbox"
                                      className="stui-checkbox-input"
                                      checked={!blocked && selected.has(tok)}
                                      onChange={() => !blocked && toggleRow(t.table, r.key)}
                                      disabled={applying || blocked}
                                      title={blocked ? r.blockedReason : undefined}
                                    />
                                    <span className={VERDICT_BADGE[r.verdict] || 'stui-badge-gray'}>
                                      {r.verdict}
                                    </span>
                                    <span className="stui-mono stui-text-xs">{r.key}</span>
                                    {hasSensitive && (
                                      <span className="stui-badge-red" title="Changes a permission or access field">
                                        <ShieldAlert size={11} /> privilege
                                      </span>
                                    )}
                                    <span className="stui-ml-auto stui-text-xs stui-text-muted">{r.changeCount} change(s)</span>
                                  </label>

                                  {blocked && (
                                    <div className="stui-row-4 stui-text-xs stui-text-warning stui-ml-xl">
                                      <Ban size={11} /> cannot apply — {r.blockedReason}
                                    </div>
                                  )}

                                  <button
                                    className="stui-btn-link stui-text-primary stui-text-xs stui-ml-xl"
                                    onClick={() => setExpandedRows(p => ({ ...p, [tok]: !rowOpen }))}
                                  >
                                    {rowOpen ? 'hide detail' : 'show detail'}
                                  </button>

                                  {rowOpen && (
                                    <div className="stui-ml-xl stui-mt-xs stui-scroll-cap">
                                      <table className="stui-inner-table">
                                        <thead>
                                          <tr><th>Path</th><th>Source</th><th>Target</th></tr>
                                        </thead>
                                        <tbody>
                                          {r.fields.slice(0, 100).map((f, i) => (
                                            <tr key={i} className={
                                              f.sensitive ? 'stui-row-tone-error stui-fw-semibold'
                                                : f.lowConfidence ? 'stui-row-tone-warning' : undefined
                                            }>
                                              <td className="stui-td-xs stui-break-word">
                                                <code>{f.path}</code>
                                                {f.sensitive && (
                                                  <span className="stui-text-error stui-ml-xs" title="Permission or access field - read this one carefully."><ShieldAlert size={12} /></span>
                                                )}
                                                {f.lowConfidence && (
                                                  <span className="stui-text-warning stui-ml-xs" title="Element identity could not be established; this entry was matched by position and may be inexact."><AlertTriangle size={12} /></span>
                                                )}
                                              </td>
                                              <td className="stui-inner-table-cell-mono stui-break-word">{f.source ?? <em>(none)</em>}</td>
                                              <td className="stui-inner-table-cell-mono stui-break-word">{f.target ?? <em>(none)</em>}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      {r.fields.length > 100 && (
                                        <div className="stui-label-note">
                                          +{r.fields.length - 100} more not shown
                                        </div>
                                      )}
                                      {r.sql && (
                                        <pre className="stui-code-block stui-mt-xs">{r.sql}</pre>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            </div>

                            {targetOnly.length > 0 && (
                              <div className="stui-inline-notice stui-mt-sm">
                                <div className="stui-row-4 stui-text-muted stui-mb-xs">
                                  <Ban size={12} />
                                  <span>
                                    Only in {diff.targetDatabase} — not actionable, never deleted
                                  </span>
                                </div>
                                {targetOnly.map(r => (
                                  <div key={r.key} className="stui-mono stui-text-muted stui-ml-lg">{r.key}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              </>
            )}

            {/* Tables barred in this direction. Listed rather than hidden — a table silently
                missing from the list is indistinguishable from a table with no differences. */}
            {diff && !diff.fatalError && blockedTables.length > 0 && (
              <div className="stui-inline-notice">
                <div className="stui-row-8 stui-fw-semibold stui-mb-xs">
                  <Ban size={13} />
                  <span>Not available in this direction</span>
                </div>
                <div className="stui-divided-list">
                  {blockedTables.map(t => (
                    <div key={t.table} className="stui-form-field">
                      <strong className="stui-mono">{t.table}</strong>
                      <span className="stui-text-muted">{t.blockedReason}</span>
                    </div>
                  ))}
                </div>
                <div className="stui-label-note">
                  Switch to <strong>Prod &rarr; PreProd</strong> to review these.
                </div>
              </div>
            )}
          </div>

          {/* ═══════════ 3: APPLY ═══════════ */}
          <div className="stui-step-panel">
            <div className="stui-step-header">
              <span className="stui-step-number">3</span>
              <h3>Apply</h3>
            </div>

            {applyResult && (
              <div className={`stui-inline-notice stui-inline-notice-${applyResult.status === 'SUCCESS' ? 'success' : 'error'}`}>
                {applyResult.status === 'SUCCESS'
                  ? <CheckCircle size={18} className="stui-td-success" />
                  : <XCircle size={18} className="stui-text-error" />}
                <div>
                  <div className="stui-fw-semibold">
                    {applyResult.status === 'SUCCESS' ? 'Applied' : applyResult.status}
                  </div>
                  <div className="stui-text-xs stui-text-muted">
                    {applyResult.errorMessage
                      ? applyResult.errorMessage
                      : `${applyResult.applied} applied, ${applyResult.skipped} skipped, ${applyResult.failed} failed`}
                  </div>
                </div>
              </div>
            )}

            {applyResult?.results?.length > 0 && (
              <>
                <div className="stui-mt-sm">
                  <table className="stui-inner-table">
                    <thead>
                      <tr><th>Table</th><th>Key</th><th>Action</th></tr>
                    </thead>
                    <tbody>
                      {applyResult.results.map((r, i) => (
                        <tr key={i}>
                          <td>{r.table}</td>
                          <td title={r.note || r.error || ''}>{r.key}</td>
                          <td>
                            {r.success
                              ? <CheckCircle size={14} className="stui-td-success" />
                              : <span className="stui-text-warning">{r.action}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="stui-btn stui-btn-primary stui-ml-auto" onClick={() => setApplyResult(null)}>
                  <RotateCcw size={16} />
                  Acknowledge &amp; Reset
                </button>
              </>
            )}

            {!applyResult && selectedRows.length === 0 && (
              <p className="stui-step-desc">
                Select rows in step 2. Nothing is selected by default, and nothing is applied
                automatically.
              </p>
            )}

            {!applyResult && selectedRows.length > 0 && diff && (
              <>
                <div>
                  <div>
                    Writing <strong>{selectedRows.length}</strong> row(s) to{' '}
                    <code className="stui-mono">{diff.targetDatabase}</code>
                  </div>
                  <ul className="stui-m-0 stui-text-xs stui-text-muted">
                    {Object.entries(perTableCounts).map(([tbl, n]) => (
                      <li key={tbl}>{tbl}: {n}</li>
                    ))}
                  </ul>
                </div>

                <button className="stui-btn stui-btn-primary" disabled={!canApply} onClick={runApply}>
                  {applying ? <Loader2 size={16} className="stui-spinning" /> : <Play size={16} />}
                  {applying
                    ? `Applying to ${diff.targetDatabase}...`
                    : `Apply ${selectedRows.length} row(s) to ${diff.targetDatabase}`}
                </button>

                <label className="stui-inline-notice stui-inline-notice-warning">
                  <input
                    type="checkbox"
                    className="stui-checkbox-input"
                    checked={acknowledged}
                    onChange={e => setAcknowledged(e.target.checked)}
                    disabled={applying}
                  />
                  <span>
                    I understand this overwrites live configuration in{' '}
                    <strong>{diff.targetDatabase}</strong> and may break the application.
                  </span>
                </label>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminConfigSyncPage;
